import React, { useEffect, useRef } from "react";
import { CheckCircle2, RefreshCw, WifiOff } from "lucide-react";
import { PostSkeleton } from "@/components/FeedSkeleton";
import { useApp } from "@/context/AppContext";

// Lot 683 — pied de liste commun à toutes les listes à défilement infini (fil, profil…). Trois états toujours visibles
// au lieu d'un vide muet quand on arrive au bout :
//   • chargement de la page suivante → squelette ;
//   • échec de la page suivante (réseau coupé une seconde, serveur en redéploiement) → message + « Réessayer », avec
//     nouvel essai automatique au retour du réseau ;
//   • fin de liste → « Tu as tout vu ✓ ».
// La sentinelle se RÉARME : un IntersectionObserver recréé à chaque fin de chargement / changement d'état, qui vérifie
// aussi la visibilité immédiatement (une sentinelle déjà à l'écran redéclenche — c'était la cause du fil « arrêté sur
// une publication » : l'observateur ne refait signe que lorsque la visibilité CHANGE).
export const FeedEnd = ({ hasMore, loadingMore, error, onLoadMore, itemCount = 0, rootMargin = "800px", testId = "feed-end" }) => {
  const { t } = useApp();
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el || !hasMore || loadingMore || error || typeof IntersectionObserver === "undefined") return undefined;
    const obs = new IntersectionObserver((entries) => { if (entries.some((e) => e.isIntersecting)) onLoadMore?.(); }, { rootMargin });
    obs.observe(el);
    return () => obs.disconnect();
  }, [hasMore, loadingMore, error, onLoadMore, rootMargin, itemCount]);

  // Réseau revenu / serveur de retour → on retente tout seul la page qui avait échoué.
  useEffect(() => {
    if (!error) return undefined;
    const retry = () => onLoadMore?.();
    ["online", "yalola:network-online", "yalola:reconnected"].forEach((e) => window.addEventListener(e, retry));
    return () => ["online", "yalola:network-online", "yalola:reconnected"].forEach((e) => window.removeEventListener(e, retry));
  }, [error, onLoadMore]);

  if (loadingMore) return <div data-testid={`${testId}-loading`}><PostSkeleton /></div>;
  if (error && hasMore) {
    return (
      <div className="yalola-card p-5 flex flex-col items-center gap-3 text-center" data-testid={`${testId}-error`}>
        <WifiOff size={20} style={{ color: "var(--yalola-text-muted)" }} />
        <p className="text-sm" style={{ color: "var(--yalola-text-muted)" }}>{t("feed.more_failed", "La suite n'a pas pu être chargée.")}</p>
        <button type="button" onClick={() => onLoadMore?.()} className="inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold text-white" style={{ background: "var(--yalola-primary)" }} data-testid={`${testId}-retry`}>
          <RefreshCw size={14} /> {t("common.retry", "Réessayer")}
        </button>
      </div>
    );
  }
  if (!hasMore && itemCount > 0) {
    return (
      <div className="py-6 flex items-center justify-center gap-2 text-sm" style={{ color: "var(--yalola-text-muted)" }} data-testid={`${testId}-done`}>
        <CheckCircle2 size={16} /> {t("feed.all_seen", "Tu as tout vu")}
      </div>
    );
  }
  // Sentinelle : 1 px, invisible — observée tant qu'il reste des pages.
  return hasMore ? <div ref={ref} style={{ height: 1 }} data-testid={`${testId}-sentinel`} /> : null;
};

export default FeedEnd;
